import React from 'react';
import { types, flow, Instance, getEnv, getParent } from 'mobx-state-tree';
import { set, toJS } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { RootStoreInstance } from '../Root';
import {
  CartBillingAddressModel,
  CartMiscModel,
  CartTotalsModel,
  CartPaymentProfileModel,
  CartPayPalInfoModel,
  CartStripeInfoModel,
  CartShippingAddressModel,
  CartShippingMethodModel,
  CartCustomerModel,
  CartCustomFieldModel,
  CartDiscountModel,
  CartAmazonInfoModel,
  PromoCodeApplyResponse,
  ICartDiscountModel,
  CartGiftCardModel,
  ICartGiftCardModel,
} from '../sub-stores/Cart';
import { CartItemModel, ICartItemModel } from '../sub-stores/CartItem';
import {
  AddressContract,
  AddressField,
  AddressFormData,
} from '../../../types/Address';
import {
  CartPaymentProfileCardContract,
  CartPaymentProfileContract,
} from '../../../types/PaymentProfile';
import { PayPalInfoContract } from '../../../types/PayPal';
import { AmazonInfoContract } from '../../../types/AmazonPay';
import {
  CartResponseWarning,
  CartUpdateContract,
  ICartService,
} from '../../../types/Cart';
import { ConfigEnabledPaymentMethodContract } from '../../../types/Configuration';
import { RootServices } from '../../../types/RootStore';
import { ShippingMethodContract } from '../../../types/ShippingMethods';
import { TokenizationRequest } from '../../../types/Tokenization';
import UtilityService from '../../../services/UtilityService';
import { CustomFieldContract } from '../../../types/CustomField';
import { APIValues } from '../../../utils/constants';
import { GiftCardContract } from '../../../types/GiftCard';
import { ItemOptionContract } from '../../../types/Item';
import { SpreedlyPostOrderResponse } from '../../../types/Spreedly';
import { StripeInfoContract } from '../../../types/Stripe';
import { OrderServicePostOrderResponse } from '../../../types/Order';
import { CartUpdateCustomerContract } from '../../../types/Customer';

export interface PaymentProfileUpdateConfig {
  cardConfig?: CartPaymentProfileCardContract;
  paypalConfig?: PayPalInfoContract;
  amazonConfig?: AmazonInfoContract;
  stripeConfig?: StripeInfoContract;
  poNumber?: string;
}

export const CartModel = types
  .model({
    cartId: types.maybeNull(types.string),
    items: types.array(CartItemModel),
    discounts: types.array(CartDiscountModel),
    giftCards: types.array(CartGiftCardModel),
    totals: types.optional(CartTotalsModel, {}),
    customer: types.optional(CartCustomerModel, {}),
    billingAddress: types.optional(CartBillingAddressModel, {}),
    shippingAddress: types.optional(CartShippingAddressModel, {}),
    shippingMethods: types.array(CartShippingMethodModel),
    paymentProfile: types.optional(CartPaymentProfileModel, {}),
    paypalInfo: types.optional(CartPayPalInfoModel, {}),
    amazonInfo: types.optional(CartAmazonInfoModel, {}),
    stripeInfo: types.optional(CartStripeInfoModel, {}),
    misc: types.optional(CartMiscModel, {}),
    customFields: types.array(CartCustomFieldModel),
  })
  .volatile(() => ({
    fetchingShippingMethods: false,
    needsNewPciToken: false,
    updatingCart: false,
    isTokenizingCard: false,
    isPopulated: false,
    useShippingForBilling: true,
    missingRequiredFields: [] as AddressField[],
    cachedSelectedShippingMethodId: undefined as number | undefined,
    cartWarnings: [] as CartResponseWarning[],
    giftCardError: undefined as string | undefined,
    quantityError: undefined as string | undefined,
    isUsingPayPalAcp: false,
    giftCardCodeBeingApplied: '',
    isLoading: false,
    loadingMessage: '' as string | React.ReactNode,
    cartToggleOpen: false,
    showEmptyCartPage: true,
    showSpreedlyChallengeModal: false,
    paymentMethodType: '',
    submitToken: null as string | null,
  }))
  .views(self => {
    return {
      get selectedShippingMethod(): ShippingMethodContract | undefined {
        return self.shippingMethods.find(method => method.selected);
      },
      get hasShippingMethodSelected(): boolean {
        return !!self.shippingMethods.find(method => method.selected);
      },
      get hasPaymentMethodSelected(): boolean {
        const isSelected = Boolean(
          self.paymentProfile.id && self.paymentProfile.id > 0
        );
        if (isSelected) {
          return Boolean(
            self.paymentProfile.method !== 'Purchase Order Number' ||
              (self.paymentProfile.poNumber &&
                self.paymentProfile.poNumber !== '')
          );
        }
        return false;
      },
      get hasStripeSecret(): boolean {
        return Boolean(self.stripeInfo.clientSecretKey);
      },
      get stripeInfoData(): StripeInfoContract {
        return self.stripeInfo;
      },
      get hasCustomerInfo(): boolean {
        return Boolean(self.customer.emailAddress && self.customer.phoneNumber);
      },
      get hasShippingAddress(): boolean {
        if (this.cartData.isAmazonPay) {
          return self.shippingAddress.hasMinimalAmazonPayAddress;
        }

        return self.shippingAddress.hasMinimalAddress;
      },
      get hasBillingAddress(): boolean {
        if (this.cartData.isAmazonPay) {
          return self.billingAddress.hasMinimalAmazonPayAddress;
        }

        return self.billingAddress.hasMinimalAddress;
      },
      get hasRequiredCustomFields(): boolean {
        return self.customFields.every(field => !field.isMissingData);
      },
      get hasItems(): boolean {
        return self.items.length !== 0;
      },
      get onlyItem(): ICartItemModel | null {
        if (self.items.length === 1) {
          return self.items[0];
        }
        return null;
      },
      get isPaymentRequired(): boolean {
        return self.totals.grandTotal > 0;
      },
      get isBillingAddressSameAsShipping(): boolean {
        if (isEmpty(self.billingAddress) || isEmpty(self.shippingAddress)) {
          return false;
        }
        const fieldsToCompare = [
          'firstName',
          'lastName',
          'address1',
          'address2',
          'city',
          'state',
          'postalCode',
          'country',
          'companyName',
          'phoneNumber',
        ];
        return isEqual(
          pick(self.billingAddress, fieldsToCompare),
          pick(self.shippingAddress, fieldsToCompare)
        );
      },
      get itemTotal(): number {
        return self.totals.items;
      },
      get discountTotal(): number {
        return self.totals.discounts;
      },
      get subTotal(): number {
        const { items, discounts, giftWrap, kitItems } = self.totals;
        const subtotal = items + discounts + giftWrap + kitItems;
        return subtotal > 0 ? subtotal : 0;
      },
      get shippingTotal(): number {
        return self.totals.shipping;
      },
      get taxTotal(): number | undefined {
        const { showTaxAndShipping } = getParent<RootStoreInstance>(self);
        const taxTotal = showTaxAndShipping ? self.totals.taxTotal : undefined;
        return taxTotal;
      },
      get grandTotal(): number {
        return self.totals.grandTotal;
      },
      get giftCardAmountUsed(): number {
        return self.totals.giftCardAmountUsed;
      },
      get cartData(): CartUpdateContract {
        return {
          customer: toJS(self.customer),
          items: toJS(self.items),
          discounts: toJS(self.discounts),
          giftCards: toJS(self.giftCards),
          paymentProfile: toJS(self.paymentProfile),
          shippingMethods: toJS(self.shippingMethods),
          shippingAddress: toJS(self.shippingAddress),
          billingAddress: toJS(self.billingAddress),
          paypalInfo: toJS(self.paypalInfo),
          stripeInfo: toJS(self.stripeInfo),
          isAmazonPay: Boolean(self.amazonInfo && self.amazonInfo.access_token),
          isPayPal: Boolean(self.paypalInfo?.payerId),
          amazonInfo: toJS(self.amazonInfo),
          totals: toJS(self.totals),
          misc: toJS(self.misc),
          customFields: toJS(self.customFields),
          submitToken: self.submitToken?.length ? self.submitToken : null,
        };
      },
      get hasRecurringBillingPaymentMethod(): boolean {
        return (
          self.paymentProfile?.method !== APIValues.PAYPAL_METHOD &&
          self.paymentProfile?.method !== APIValues.AMAZON_PAY_METHOD
        );
      },
      get hasFreeShippingDiscount(): boolean {
        return Boolean(
          self.discounts.find(discount => discount.discountType === 0)
        );
      },
      get canPlaceOrder(): boolean {
        // This property uses `this` instead of `self` because it is
        // referring to other properties within the same views object
        // and it is not available in the `self` context.
        const isPaymentSatisfied =
          (this.hasPaymentMethodSelected && !self.needsNewPciToken) ||
          !this.isPaymentRequired ||
          self.isUsingPayPalAcp;

        return (
          this.hasCustomerInfo &&
          this.hasShippingAddress &&
          this.hasBillingAddress &&
          this.hasShippingMethodSelected &&
          isPaymentSatisfied &&
          this.hasRequiredCustomFields
        );
      },
      get customerPhoneNumber(): string | null {
        return (
          self.shippingAddress.phoneNumber ?? self.billingAddress.phoneNumber
        );
      },
      get shippingAddressState(): string | null {
        return self.shippingAddress.state;
      },
      get shippingAddressCountry(): string | null {
        return self.shippingAddress.country;
      },
      get appliedDiscounts(): ICartDiscountModel[] {
        return (self.discounts || []).filter(discount => discount.id);
      },
      get appliedGiftCards(): ICartGiftCardModel[] {
        return (self.giftCards || []).filter(
          ({ code }) => code !== self.giftCardCodeBeingApplied
        );
      },
      get appliedCouponCodes(): string[] {
        return (self.discounts || [])
          .filter(discount => discount.id && discount.couponCode)
          .map(discount => discount.couponCode);
      },
      get appliedGiftCardCodes(): string[] {
        return (self.giftCards || []).map(giftCard => giftCard.code);
      },
      get hasRecurringBillingItem(): boolean {
        return !!self.items?.find(
          item => item.pricing.recurringPrice.everyXMonths !== ''
        );
      },
      hasDiscount(discountToSearch: ICartDiscountModel): boolean {
        return !!self.discounts.find(
          discount =>
            discount.id.toLowerCase() === discountToSearch.id.toLowerCase()
        );
      },
    };
  })
  .actions(self => {
    const { cartService, giftCardService, orderService, tokenizationService } =
      getEnv<RootServices>(self);
    const setIsUsingPayPalAcp = (isUsingPayPalAcp = false): void => {
      set(self, { isUsingPayPalAcp });
    };
    const setCartToggleOpen = (isOpen = false): void => {
      set(self, { cartToggleOpen: isOpen });
    };
    const setPaymentMethodType = (paymentMethodType: string) => {
      set(self, { paymentMethodType });
    };
    const setIsLoading = (
      isLoading: boolean,
      loadingMessage: string | React.ReactNode = ''
    ): void => {
      set(self, { isLoading, loadingMessage });
    };
    const updateSpreedlyTransactionDetails = (
      transactionDetails: Pick<
        CartPaymentProfileCardContract,
        'transactionToken' | 'challengeResponse' | 'browserInfo'
      >
    ) => {
      set(self, {
        paymentProfile: {
          ...self.paymentProfile,
          card: {
            ...self.paymentProfile.card,
            ...transactionDetails,
          },
        },
      });
    };
    const setShowSpreedlyChallengeModal = (showModal = false): void => {
      set(self, { showSpreedlyChallengeModal: showModal });
    };
    const preSelectShippingMethodIfNeeded = (
      shippingMethods: ShippingMethodContract[]
    ): ShippingMethodContract[] => {
      if (
        (shippingMethods || []).length &&
        !shippingMethods.some(method => method.selected)
      ) {
        return shippingMethods.map((method, index) => {
          if (index > 0) {
            return method;
          }
          return { ...method, selected: true };
        });
      }
      return shippingMethods;
    };
    const setCustomFieldDefaultValues = (
      customFields: CustomFieldContract[]
      // ): CustomFieldContract[] => customFields.map(customField => customField);
    ): CustomFieldContract[] => {
      return (customFields ?? []).map(customField => {
        // TODO: Refactor this, throws error if merchant doesnt have dropdown values defined
        if (customField.inputType.toUpperCase() === 'DROPDOWN') {
          const value =
            customField.value !== ''
              ? customField.value
              : customField.dropdownValues[0].value;
          return {
            ...customField,
            value,
          };
        }
        return customField;
      });
    };

    const mutateCartUpdate = (
      cartUpdate: CartUpdateContract
    ): CartUpdateContract => {
      return {
        ...cartUpdate,
        customer: {
          ...cartUpdate.customer,
          phoneNumber:
            cartUpdate?.customer?.phoneNumber || self.customer.phoneNumber,
          emailAddress:
            cartUpdate.customer?.emailAddress || self.customer.emailAddress,
          emailOptIn:
            cartUpdate.customer?.emailOptIn || self.customer.emailOptIn,
        },
        shippingAddress: {
          ...cartUpdate.shippingAddress,
          phoneNumber:
            cartUpdate.shippingAddress?.phoneNumber ||
            self.shippingAddress.phoneNumber,
        },
        amazonInfo: cartUpdate.amazonInfo ?? { ...self.amazonInfo },
        paypalInfo: cartUpdate.paypalInfo ?? { ...self.paypalInfo },
        paymentProfile: self.paymentProfile,
        customFields: setCustomFieldDefaultValues(cartUpdate.customFields),
        shippingMethods: preSelectShippingMethodIfNeeded(
          cartUpdate.shippingMethods
        ),
      };
    };

    const updateCartFromApiResponse = (
      response: CartUpdateContract,
      fieldsToUpdate: Partial<CartUpdateContract & { isPopulated: boolean }>
    ): void => {
      const cartUpdate = {
        ...mutateCartUpdate(response),
        /// The GET call returns null for the address fields and this causes issues with PayPal
        ...fieldsToUpdate,
        stripeInfo: self.cartData.stripeInfo,
      };
      set(self, cartUpdate);
      set(self, {
        cachedSelectedShippingMethodId: self.shippingMethods.find(
          method => method.selected
        )?.id,
      });
      set(self, { cartWarnings: response.warnings ?? [] });
    };

    const getCartFlow = flow(function* () {
      try {
        const result = (yield cartService.getCartDataAsync(
          self.cartId
        )) as CartUpdateContract;
        updateCartFromApiResponse(result, {
          billingAddress: self.cartData.billingAddress,
          shippingAddress: self.cartData.shippingAddress,
          isPopulated: true,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        set(self, { isPopulated: true, items: [] });
      }
    });

    const updateCartFlow = flow(function* () {
      try {
        let result: CartUpdateContract = yield cartService.postCartDataAsync(
          self.cartId,
          self.cartData
        );

        const initialShippingMethodId =
          self.cachedSelectedShippingMethodId ??
          result.shippingMethods.find(method => method.selected)?.id;
        let cartUpdate = {
          ...mutateCartUpdate(result),
          stripeInfo: self.cartData.stripeInfo,
          isPopulated: true,
        };

        if (result.shippingMethods.length) {
          const shippingWasAutoSelected =
            initialShippingMethodId !==
            cartUpdate.shippingMethods.find(method => method.selected)?.id;

          if (shippingWasAutoSelected) {
            result = yield cartService.postCartDataAsync(
              self.cartId,
              cartUpdate
            );
            cartUpdate = {
              ...mutateCartUpdate(result),
              stripeInfo: self.cartData.stripeInfo,
              isPopulated: true,
            };
          }
        }

        set(self, cartUpdate);
        set(self, { cartWarnings: result.warnings ?? [] });
        set(self, {
          cachedSelectedShippingMethodId: self.shippingMethods.find(
            method => method.selected
          )?.id,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        set(self, { items: [] });
      }
    });

    const updateQuantityFlow = flow(function* (
      itemId: number,
      quantity: number
    ) {
      set(self, {
        quantityError: undefined,
        isLoading: true,
        loadingMessage: 'Updating quantity...',
      });

      try {
        let hasQuantityChanged = false;
        set(self, {
          items: self.cartData.items.map(item => {
            if (item.id === itemId && item.qty !== quantity) {
              hasQuantityChanged = true;
              return { ...item, qty: quantity };
            }
            return item;
          }),
        });

        if (!hasQuantityChanged) {
          set(self, { isLoading: false });
          set(self, { loadingMessage: '' });
          return;
        }

        const errors = yield cartService.updateQuantity(self.cartData);

        if (errors.length > 0) {
          set(self, { quantityError: errors[0].Message });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        set(self, { isLoading: false });
        set(self, { loadingMessage: '' });

        throw new Error('Error updating quantity');
      }

      const response = yield cartService.getCartDataAsync(self.cartId);

      updateCartFromApiResponse(response, {
        billingAddress: self.cartData.billingAddress,
        shippingAddress: self.cartData.shippingAddress,
        shippingMethods: self.cartData.shippingMethods,
        paymentProfile: self.cartData.paymentProfile,
        paypalInfo: self.cartData.paypalInfo,
        amazonInfo: self.cartData.amazonInfo,
        misc: self.cartData.misc,
        customFields: self.cartData.customFields,
        giftCards: self.cartData.giftCards,
        isPopulated: true,
      });

      yield updateCartFlow();
    });

    const resetLoadingVolatiles = () => {
      set(self, { isLoading: false, loadingMessage: '' });
    };

    const removeItemFlow = flow(function* (
      itemId: number,
      itemOptions: ItemOptionContract[]
    ) {
      set(self, { isLoading: true, loadingMessage: 'Removing Cart Item...' });

      if (self.onlyItem) {
        set(self, { showEmptyCartPage: false });
      }

      try {
        const items = self.cartData.items.map(item => {
          if (
            item.id === itemId &&
            JSON.stringify(item.options) === JSON.stringify(itemOptions)
          ) {
            return { ...item, qty: 0 };
          }
          return item;
        });

        set(self, {
          items,
        });

        const errors = yield cartService.updateQuantity(self.cartData);

        if (errors.length > 0) {
          set(self, { quantityError: errors[0].Message });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        set(self, { isLoading: false });
        set(self, { loadingMessage: '' });
        throw new Error('Error removing cart item');
      }

      const response = yield cartService.getCartDataAsync(self.cartId);

      updateCartFromApiResponse(response, {
        billingAddress: self.cartData.billingAddress,
        shippingAddress: self.cartData.shippingAddress,
        shippingMethods: self.cartData.shippingMethods,
        paymentProfile: self.cartData.paymentProfile,
        paypalInfo: self.cartData.paypalInfo,
        amazonInfo: self.cartData.amazonInfo,
        misc: self.cartData.misc,
        customFields: self.cartData.customFields,
        giftCards: self.cartData.giftCards,
        isPopulated: true,
      });

      yield updateCartFlow();
    });

    const applyGiftCardFlow = flow(function* (giftCardCode: string) {
      set(self, { giftCardError: undefined });
      set(self, { giftCardCodeBeingApplied: giftCardCode });
      try {
        const result = (yield giftCardService.validateGiftCardCode(
          giftCardCode
        )) as GiftCardContract;

        if (result === null) {
          set(self, { giftCardError: 'Gift Card Code is invalid' });
          return;
        }
        if (result.value === 0) {
          set(self, {
            giftCardError: `Sorry, gift card ${giftCardCode} has zero balance`,
          });
          return;
        }
        set(self, { giftCards: [...self.giftCards, result] });
        yield updateCartFlow();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        set(self, { items: [] });
      }
      set(self, { giftCardCodeBeingApplied: '' });
    });

    const removeGiftCardFlow = flow(function* (giftCardCode: string) {
      set(self, { giftCardError: undefined });
      const updatedGiftCards = self.cartData.giftCards.filter(
        giftCard => giftCard.code !== giftCardCode
      );
      set(self, { giftCards: updatedGiftCards });
      yield updateCartFlow();
    });

    const getPromoCodeApplyResult = (
      promoCode: string
    ): PromoCodeApplyResponse => {
      const discountApplied = !!self.discounts.find(
        discount =>
          discount.couponCode.toLowerCase() === promoCode.toLowerCase()
      );
      const couponApplicationMessage = self.cartWarnings.find(
        warning => warning.Code === 'couponCode'
      );
      return {
        applied: discountApplied,
        errorMessage: couponApplicationMessage?.Message,
      };
    };

    const applyPromoCode = flow(function* (couponCode: string) {
      const warnings = yield cartService.applyDiscount(
        couponCode,
        self.cartData
      );
      if (
        (warnings || []).length === 0 ||
        (warnings || []).some((warning: CartResponseWarning) =>
          warning.Message.includes(
            'it has been removed and the new coupon has been added to your cart.'
          )
        )
      ) {
        const response = yield cartService.getCartDataAsync(self.cartId);

        updateCartFromApiResponse(response, {
          billingAddress: self.cartData.billingAddress,
          shippingAddress: self.cartData.shippingAddress,
          shippingMethods: self.cartData.shippingMethods,
          paymentProfile: self.cartData.paymentProfile,
          paypalInfo: self.cartData.paypalInfo,
          amazonInfo: self.cartData.amazonInfo,
          misc: self.cartData.misc,
          customFields: self.cartData.customFields,
          giftCards: self.cartData.giftCards,
          isPopulated: true,
        });

        if (self.shippingMethods.length > 0) {
          yield updateCartFlow();

          const freeShippingMethod = self.shippingMethods.find(
            method => method.system.toUpperCase() === 'FREE'
          );

          if (self.hasFreeShippingDiscount && freeShippingMethod) {
            self.shippingMethods.map(method => {
              if (method.id === freeShippingMethod.id) {
                return { ...method, selected: true };
              }
              return { ...method, selected: false };
            });

            yield updateCartFlow();
          }
        }
      }

      set(self, { cartWarnings: warnings ?? [] });
    });

    const removePromoCode = flow(function* (
      discountToRemove: ICartDiscountModel,
      isSiteDesignerEnabled: boolean
    ) {
      yield cartService.removeDiscount(
        discountToRemove.couponCode,
        self.cartId,
        isSiteDesignerEnabled
      );

      const result = (yield cartService.getCartDataAsync(
        self.cartId
      )) as CartUpdateContract;

      updateCartFromApiResponse(result, {
        billingAddress: self.cartData.billingAddress,
        shippingAddress: self.cartData.shippingAddress,
        shippingMethods: self.cartData.shippingMethods,
        paymentProfile: self.cartData.paymentProfile,
        paypalInfo: self.cartData.paypalInfo,
        amazonInfo: self.cartData.amazonInfo,
        misc: self.cartData.misc,
        customFields: self.cartData.customFields,
        giftCards: self.cartData.giftCards,
        isPopulated: true,
      });

      if (self.shippingMethods.length > 0) {
        yield updateCartFlow();
      }
    });

    const placeOrder = async (): Promise<OrderServicePostOrderResponse> => {
      return orderService.postOrderDataAsync(self.cartId, self.cartData);
    };

    const spreedlyPlaceOrder = async (): Promise<SpreedlyPostOrderResponse> => {
      return orderService.spreedlyPostOrderDataAsync(
        self.cartId,
        self.cartData
      );
    };
    const tokenizeCard = async (
      request: TokenizationRequest
    ): Promise<string | null> => {
      return tokenizationService.getTokenAsync(request);
    };
    const tokenizeNewCVV = async (cvv: string): Promise<void> => {
      const token = self.paymentProfile.card.pCIaaSId;
      if (!token) {
        throw new Error('Saved card is missing token and cannot be used');
      }
      return tokenizationService.updateCVVAsync({ token, cvv });
    };
    const setCartId = (cartId: string): void => {
      set(self, { cartId });
    };
    const setFetchingShippingMethods = (isFetching = false): void => {
      set(self, { updatingCart: isFetching });
      set(self, { fetchingShippingMethods: isFetching });
    };
    const setUpdatingCart = (isUpdating = false): void => {
      set(self, { updatingCart: isUpdating });
    };
    const setIsTokenizingCard = (isTokenizingCard = false): void => {
      set(self, { isTokenizingCard });
    };
    const setNeedsNewPciToken = (needsNewPciToken = false): void => {
      set(self, { needsNewPciToken });
    };
    const updateShippingAddress = (
      newAddress: AddressContract | AddressFormData
    ): void => {
      set(self.shippingAddress, { ...newAddress, preferred: true });
    };
    const updateBillingAddress = (newAddress: AddressContract): void => {
      set(self.billingAddress, { ...newAddress, preferred: true });
    };

    const setBillingAddressToShippingValues = (): void => {
      const { shippingAddress } = self.cartData;
      delete shippingAddress.residential;
      updateBillingAddress(shippingAddress);
    };
    const clearBillingAddress = (): void => {
      const emptyBillingAddress = CartBillingAddressModel.create();
      updateBillingAddress(emptyBillingAddress);
    };
    const setUseShippingForBilling = (useShipping: boolean): void => {
      set(self, { useShippingForBilling: useShipping });
      if (useShipping) {
        setBillingAddressToShippingValues();
      }
    };
    const setShippingMethods = (
      shippingMethods: ShippingMethodContract[]
    ): void => {
      self.shippingMethods.clear();
      shippingMethods.forEach(method => {
        self.shippingMethods.push({
          ...method,
        });
      });
    };
    const updateShippingMethod = (shippingMethodId: number): void => {
      const updatedMethods = self.cartData.shippingMethods.map(method => {
        return {
          ...method,
          selected: method.id === shippingMethodId,
        };
      });

      set(self.shippingMethods, updatedMethods);
      set(self, { cachedSelectedShippingMethodId: shippingMethodId });
    };

    const updatePaymentProfile = (
      newMethod: ConfigEnabledPaymentMethodContract,
      paymentConfig: PaymentProfileUpdateConfig = {}
    ): void => {
      const { cardConfig, paypalConfig, amazonConfig, stripeConfig, poNumber } =
        paymentConfig;

      const paymentProfileUpdate: CartPaymentProfileContract = {
        id: newMethod.id,
        method: newMethod.methodType,
        poNumber: UtilityService.html5Encode(poNumber),
        preferred: true,
        card: cardConfig ?? {
          pCIaaSId: null,
          spreedlyPaymentToken: null,
          paymentMethodFingerprint: null,
          cardNumber: null,
          last4: null,
          cardBrand: null,
          cardToken: null,
          cardTokenProvider: null,
          cardType: newMethod.id,
          cardTypeName: newMethod.methodType,
          holdersName: null,
          expMonth: null,
          expYear: null,
          id: null,
          issueNumber: null,
          issueMonth: null,
          issueYear: null,
          browserInfo: null,
          transactionToken: null,
          challengeResponse: null,
        },
      };

      const paypalInfoUpdate: PayPalInfoContract = {
        ...self.cartData.paypalInfo,
        transactionId: null,
        payerId: null,
        token: null,
        paymentStatus: null,
        ...paypalConfig,
      };

      if (paypalConfig && paymentProfileUpdate.card) {
        paymentProfileUpdate.card.holdersName = `${self.shippingAddress.firstName} ${self.shippingAddress.lastName}`;
      }
      const nameFormat = (
        first: string | null,
        last: string | null
      ): string => {
        return `${first || ''} ${last || ''}`;
      };
      if (stripeConfig && paymentProfileUpdate.card) {
        set(self.stripeInfo, {
          ...stripeConfig,
          isSavedCard: stripeConfig?.isSavedCard ?? false,
        });
        paymentProfileUpdate.card.holdersName = self.hasBillingAddress
          ? nameFormat(
              self.billingAddress.firstName,
              self.billingAddress.lastName
            )
          : nameFormat(
              self.shippingAddress.firstName,
              self.shippingAddress.lastName
            );
        paymentProfileUpdate.card.cardTypeName = stripeConfig.cardBrand ?? null;
        paymentProfileUpdate.card.cardBrand = stripeConfig.cardBrand ?? null;
        paymentProfileUpdate.card.expMonth = stripeConfig.expMonth ?? null;
        paymentProfileUpdate.card.expYear = stripeConfig.expYear ?? null;
        paymentProfileUpdate.card.last4 = stripeConfig.last4 ?? null;
        paymentProfileUpdate.card.cardToken =
          stripeConfig.paymentMethodId ?? null;
        paymentProfileUpdate.card.cardTokenProvider = 'STRIPE';
      }

      const amazonInfoUpdate: AmazonInfoContract = {
        // eslint-disable-next-line
        access_token: null,
        orderReferenceId: null,
        ...amazonConfig,
      };

      set(self.paymentProfile, paymentProfileUpdate);
      set(self.paypalInfo, paypalInfoUpdate);
      set(self.amazonInfo, amazonInfoUpdate);
    };

    const resetPayPalPaymentProfile = () => {
      const nullPaymentProfile: CartPaymentProfileContract = {
        id: null,
        method: null,
        poNumber: null,
        preferred: null,
        card: {
          pCIaaSId: null,
          cardNumber: null,
          last4: null,
          cardType: null,
          cardTypeName: null,
          holdersName: null,
          expMonth: null,
          expYear: null,
          id: null,
          issueNumber: null,
          issueMonth: null,
          issueYear: null,
        },
      };

      const nullPaypalInfo: PayPalInfoContract = {
        transactionId: null,
        payerId: null,
        token: null,
        paymentStatus: null,
        paymentMethodId: -1,
        totalAmount: 0,
        useStoreCredit: false,
      };

      set(self.paymentProfile, nullPaymentProfile);
      set(self.paypalInfo, nullPaypalInfo);
    };

    const updateCustomer = (customer: CartUpdateCustomerContract): void => {
      const { emailAddress, phoneNumber, emailOptIn } = customer;

      const customerUpdate = {
        emailAddress: emailAddress ?? self.customer.emailAddress,
        phoneNumber: phoneNumber ?? self.customer.phoneNumber,
        emailOptIn:
          emailOptIn !== undefined ? emailOptIn : self.customer.emailOptIn,
      };
      set(self.customer, customerUpdate);
    };

    const updateComment = (comment: string): void => {
      set(self.misc, { comment });
    };

    const updateCustomField = (
      customFieldId: number,
      newValue: string
    ): void => {
      const targetField = self.customFields.find(
        field => field.id === customFieldId
      );
      if (targetField) {
        targetField.updateValue(newValue);
      }
    };

    const setMissingRequiredFields = (
      missingFields: AddressField[] = []
    ): void => {
      set(self, { missingRequiredFields: missingFields });
    };

    const updateStripeInfo = (stripeInfo: StripeInfoContract): void => {
      set(self, {
        stripeInfo: {
          ...self.stripeInfo,
          ...stripeInfo,
        },
      });
    };

    const updatePaymentProfileWithStripeInfo = (): void => {
      set(self, {
        paymentProfile: {
          ...self.paymentProfile,
          card: {
            ...self.paymentProfile.card,
            cardBrand: self.stripeInfo.cardBrand,
            last4: self.stripeInfo.last4,
            cardToken: self.stripeInfo.paymentMethodId,
            cardTokenProvider: 'STRIPE',
          },
        },
      });
    };

    const formatExpiration = (value: number, isMonth: boolean): string => {
      return isMonth ? `0${value}`.slice(-2) : `20${value}`.slice(-4);
    };

    const updateCardExpiration = (expMonth: number, expYear: number): void => {
      set(self.paymentProfile, {
        ...self.paymentProfile,
        card: {
          ...self.paymentProfile.card,
          expMonth: formatExpiration(expMonth, true),
          expYear: formatExpiration(expYear, false),
        },
      });
    };

    const updateSubmitToken = (value: string | null = null) => {
      set(self, { submitToken: value });
    };

    const getCartService = (): ICartService => cartService;

    return {
      getCartService,
      applyGiftCardFlow,
      applyPromoCode,
      clearBillingAddress,
      getCartFlow,
      updateCartFlow,
      getPromoCodeApplyResult,
      setCartId,
      setFetchingShippingMethods,
      setUpdatingCart,
      setIsTokenizingCard,
      setNeedsNewPciToken,
      placeOrder,
      setMissingRequiredFields,
      removeGiftCardFlow,
      removePromoCode,
      removeItemFlow,
      tokenizeCard,
      tokenizeNewCVV,
      updateShippingAddress,
      updateBillingAddress,
      setBillingAddressToShippingValues,
      setUseShippingForBilling,
      updateShippingMethod,
      updateStripeInfo,
      updatePaymentProfile,
      updateCardExpiration,
      updateSpreedlyTransactionDetails,
      updateCustomer,
      updateComment,
      updateCustomField,
      setIsUsingPayPalAcp,
      resetPayPalPaymentProfile,
      updateQuantityFlow,
      setCartToggleOpen,
      spreedlyPlaceOrder,
      setShowSpreedlyChallengeModal,
      setPaymentMethodType,
      resetLoadingVolatiles,
      updatePaymentProfileWithStripeInfo,
      updateSubmitToken,
      setShippingMethods,
      setIsLoading,
    };
  });

export interface ICartModel extends Instance<typeof CartModel> {}
